import { GraviButton, Horizontal, MaterialIcon, Texto, Vertical } from '@gravitate-js/excalibrr'
import { useQueryClient } from '@tanstack/react-query'
import { Avatar, Card, Divider, Empty, Input, Popconfirm, Tooltip } from 'antd'
import dayjs from 'dayjs'
import React, { useState } from 'react'

import type { InvoiceComment } from '../../../../../api/useFreight/types'
import { useInvoices } from '../../../../../api/useFreight/useInvoices'
import { useSecurityContext } from '../../../../Navigation/SecurityContext'
import { timeDisplay } from '../../../shared/util/timeDisplay'

interface InvoiceCommentsContainerProps {
  invoiceGroupId: string
}

export function InvoiceCommentsContainer({ invoiceGroupId }: InvoiceCommentsContainerProps) {
  // Use the useInvoiceById hook to get the invoice group data
  const { useInvoiceById } = useInvoices()
  const { data: invoiceGroup, isLoading } = useInvoiceById(invoiceGroupId)

  // Get comments from the invoice group
  const comments = invoiceGroup?.comments || []

  // Sort comments by timestamp (newest first)
  const sortedComments = [...comments].sort((a, b) => {
    return new Date(b.created_on).getTime() - new Date(a.created_on).getTime()
  })

  if (isLoading) {
    return (
      <Vertical verticalCenter className='p-3'>
        <Texto>Loading comments...</Texto>
      </Vertical>
    )
  }

  return <InvoiceCommentsPanel comments={sortedComments} invoiceGroupId={invoiceGroupId} />
}

function InvoiceCommentsPanel({
  comments,
  invoiceGroupId,
}: {
  comments: InvoiceComment[]
  invoiceGroupId: string
}) {
  const [newComment, setNewComment] = useState('')
  const { useAddCommentMutation, useRemoveCommentMutation } = useInvoices()
  const addCommentMutation = useAddCommentMutation()
  const deleteCommentMutation = useRemoveCommentMutation()
  const { user } = useSecurityContext()

  const queryClient = useQueryClient()

  const handleAddComment = async () => {
    if (!newComment.trim()) return

    try {
      await addCommentMutation.mutateAsync({
        invoice_group_id: invoiceGroupId,
        msg: newComment.trim(),
      })
      setNewComment('')

      // Invalidate the invoice query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceGroupId] })
      queryClient.invalidateQueries({ queryKey: ['invoices-be-query'] })
    } catch (error) {
      console.error('Failed to add comment:', error)
    }
  }

  const handleDeleteComment = async (commentId: string) => {
    try {
      await deleteCommentMutation.mutateAsync({
        invoice_group_id: invoiceGroupId,
        comment_id: commentId,
      })

      // Invalidate the invoice query to refresh the data
      queryClient.invalidateQueries({ queryKey: ['invoice', invoiceGroupId] })
      queryClient.invalidateQueries({ queryKey: ['invoices-be-query'] })
    } catch (error) {
      console.error('Failed to delete comment:', error)
    }
  }

  return (
    <Vertical style={{ backgroundColor: 'var(--gray-100)' }} gap={24}>
      <Vertical verticalCenter={comments.length === 0} className='p-3' flex={1} style={{ overflowY: 'auto' }} gap={12}>
        {comments.length === 0 ? (
          <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description='No comments yet' style={{ margin: '24px 0' }} />
        ) : (
          comments.map((comment) => {
            const isCurrentUser = user?.username === comment.username
            const commentDate = dayjs(comment.created_on)
            const timeInfo = timeDisplay(comment.created_on)

            return (
              <Card
                key={comment.id}
                size='small'
                bordered={false}
                styles={{
                  body: {
                    padding: 12,
                    border: '1px solid var(--gray-200)',
                    borderLeft: isCurrentUser ? '5px solid var(--theme-color-2)' : 'none',
                    borderRadius: 8,
                  },
                }}
              >
                <Horizontal verticalCenter gap={6}>
                  <Avatar
                    style={{
                      backgroundColor: isCurrentUser ? 'var(--theme-color-2)' : 'var(--dark-gray)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: 24,
                    }}
                    size='small'
                  >
                    {comment.username.charAt(0).toUpperCase()}
                  </Avatar>

                  <Texto
                    style={{
                      flex: 1,
                      whiteSpace: 'nowrap',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {comment.username}
                  </Texto>
                  <Tooltip title={commentDate.format('MMMM D, YYYY h:mm A')}>
                    <Texto style={{ flex: 0, whiteSpace: 'nowrap' }} category='label' appearance='hint'>
                      {timeInfo.display}
                    </Texto>
                  </Tooltip>
                  {isCurrentUser && (
                    <Popconfirm
                      title='Delete this comment?'
                      description='This action cannot be undone.'
                      onConfirm={() => handleDeleteComment(comment.id)}
                      okText='Delete'
                      cancelText='Cancel'
                      placement='left'
                      okButtonProps={{ danger: true }}
                    >
                      <GraviButton size='small' appearance='filled' icon={<MaterialIcon icon='delete' />} />
                    </Popconfirm>
                  )}
                </Horizontal>

                <Divider style={{ margin: '6px 0' }} />

                <Texto style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{comment.text}</Texto>
              </Card>
            )
          })
        )}
      </Vertical>
      <Vertical style={{ flex: 0, overflow: 'visible' }} bg='elevated'>
        <Card title='Add a Comment' size='small' bordered={false}>
          <Vertical gap={12}>
            <Input.TextArea
              placeholder='Type your comment here...'
              rows={3}
              value={newComment}
              onChange={(e) => setNewComment(e.target.value)}
              style={{ borderRadius: '4px' }}
              count={{
                show: false,
                max: 300,
                exceedFormatter: (value) => value.slice(0, 300),
              }}
            />
            <Horizontal justifyContent='flex-end'>
              <GraviButton
                theme2
                buttonText='Add Comment'
                onClick={handleAddComment}
                disabled={!newComment.trim() || addCommentMutation.isPending}
                loading={addCommentMutation.isPending}
                icon={<MaterialIcon icon='add_comment' />}
              />
            </Horizontal>
          </Vertical>
        </Card>
      </Vertical>
    </Vertical>
  )
}
